import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArchiveBoxIcon,
  ArrowLeftIcon,
  ArrowPathIcon,
  ArrowUpTrayIcon,
  ClipboardDocumentIcon,
  CubeIcon,
  LinkIcon,
  NoSymbolIcon,
  PencilSquareIcon,
  TrashIcon,
  UserPlusIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { projectService } from '../services/projectService';
import { shareLinkState, shareService } from '../services/shareService';
import { formatDate } from '../lib/format';
import { Project, ProjectMember, ProjectStatus, Release, ShareLink } from '../types';
import AddMemberModal from '../components/AddMemberModal';
import CreateReleaseModal from '../components/CreateReleaseModal';
import ReleaseCard from '../components/ReleaseCard';
import ConfirmDialog from '../components/ConfirmDialog';
import PublicDownloadsModal from '../components/PublicDownloadsModal';
import { Avatar, EmptyState, ErrorNote, Field, LoadingState, Modal, StatusBadge } from '../components/ui';

type Tab = 'releases' | 'members' | 'sharing' | 'settings';

/** Suggests the next patch version, e.g. 1.4.2 → 1.4.3. */
const nextVersion = (latest?: string) => {
  const match = latest?.match(/^(.*?)(\d+)$/);
  return match ? `${match[1]}${Number(match[2]) + 1}` : '';
};

const ProjectDetail: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { user, isAdmin } = useAuth();

  const [project, setProject] = useState<Project | null>(null);
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [releases, setReleases] = useState<Release[]>([]);
  const [shareLinks, setShareLinks] = useState<ShareLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>('releases');

  const [showAddMember, setShowAddMember] = useState(false);
  const [showCreateRelease, setShowCreateRelease] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<ProjectMember | null>(null);
  const [removingMember, setRemovingMember] = useState(false);
  const [showEditProject, setShowEditProject] = useState(false);
  const [statusAction, setStatusAction] = useState<ProjectStatus | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showCreateLink, setShowCreateLink] = useState(false);
  const [linkToDeactivate, setLinkToDeactivate] = useState<ShareLink | null>(null);
  const [visitorsLink, setVisitorsLink] = useState<ShareLink | null>(null);

  const canManage = !!project && (isAdmin || project.createdBy === user?.uid);

  const load = useCallback(async () => {
    if (!projectId || !user) return;
    setLoadError(false);
    try {
      const [projectData, releasesData] = await Promise.all([
        projectService.getProject(projectId),
        projectService.getProjectReleases(projectId).catch(() => [] as Release[]),
      ]);
      setProject(projectData);
      setReleases(releasesData);

      const manager = !!projectData && (isAdmin || projectData.createdBy === user.uid);
      if (!manager) {
        setMembers([]);
        setShareLinks([]);
        return;
      }

      let [membersData, linksData] = await Promise.all([
        projectService.getProjectMembers(projectId),
        shareService.getProjectShareLinks(projectId),
      ]);

      // Bring data saved before the security update in line with the new rules.
      const legacyMembers = membersData.some((m) => m.id !== `${projectId}_${m.email.toLowerCase()}`);
      const legacyLinks = linksData.some((l) => l.id !== l.hash);
      if (legacyMembers || legacyLinks) {
        try {
          if (legacyMembers) await projectService.migrateLegacyMembers(projectId, user.uid);
          if (legacyLinks) await shareService.migrateLegacyLinks(projectId);
          [membersData, linksData] = await Promise.all([
            projectService.getProjectMembers(projectId),
            shareService.getProjectShareLinks(projectId),
          ]);
        } catch (error) {
          console.error('Could not migrate legacy project data:', error);
        }
      }
      setMembers(membersData);
      setShareLinks(linksData);
    } catch (error) {
      console.error('Error loading project data:', error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [projectId, user, isAdmin]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  const releaseVersions = useMemo(() => new Map(releases.map((r) => [r.id, r.version])), [releases]);

  if (loading) return <LoadingState />;

  if (loadError || !project) {
    return (
      <div className="card mx-auto max-w-lg">
        <EmptyState
          icon={NoSymbolIcon}
          title={loadError ? t('projectDetail.loadFailed') : t('projectDetail.notFound')}
          text={loadError ? undefined : t('projectDetail.notFoundText')}
          action={
            <button type="button" className="btn btn-secondary" onClick={() => navigate('/projects')}>
              {t('projectDetail.backToProjects')}
            </button>
          }
        />
      </div>
    );
  }

  const changeStatus = async (status: ProjectStatus) => {
    setActionLoading(true);
    try {
      await projectService.setProjectStatus(project.id, status);
      await shareService.syncPublicProject(project.id).catch((e) => console.warn(e));
      toast.success(t(`projectDetail.statusChanged.${status}`));
      setStatusAction(null);
      await load();
    } catch (error) {
      console.error('Error changing project status:', error);
      toast.error(t('common.genericError'));
    } finally {
      setActionLoading(false);
    }
  };

  const removeMember = async () => {
    if (!memberToRemove) return;
    setRemovingMember(true);
    try {
      await projectService.removeMember(memberToRemove.id);
      toast.success(t('members.removed', { name: memberToRemove.name }));
      setMemberToRemove(null);
      await load();
    } catch (error) {
      console.error('Error removing member:', error);
      toast.error(t('members.removeFailed'));
    } finally {
      setRemovingMember(false);
    }
  };

  const copyLink = async (hash: string) => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/share/${hash}`);
      toast.success(t('share.copied'));
    } catch {
      toast.error(t('share.copyFailed'));
    }
  };

  const deactivateLink = async () => {
    if (!linkToDeactivate) return;
    setActionLoading(true);
    try {
      await shareService.deactivateShareLink(linkToDeactivate);
      toast.success(t('share.deactivated'));
      setLinkToDeactivate(null);
      await load();
    } catch (error) {
      console.error('Error deactivating share link:', error);
      toast.error(t('common.genericError'));
    } finally {
      setActionLoading(false);
    }
  };

  const status = project.status || 'active';
  const tabs: { id: Tab; label: string; count?: number; icon: React.ComponentType<React.SVGProps<SVGSVGElement>> }[] = [
    { id: 'releases', label: t('projectDetail.tabs.releases'), count: releases.length, icon: CubeIcon },
    ...(canManage
      ? [
          { id: 'members' as Tab, label: t('projectDetail.tabs.members'), count: members.length, icon: UsersIcon },
          {
            id: 'sharing' as Tab,
            label: t('projectDetail.tabs.sharing'),
            count: shareLinks.filter((l) => shareLinkState(l) === 'ok').length,
            icon: LinkIcon,
          },
          { id: 'settings' as Tab, label: t('projectDetail.tabs.settings'), icon: PencilSquareIcon },
        ]
      : []),
  ];

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/projects"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          {t('nav.projects')}
        </Link>
        <div className="mt-3 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2.5">
              <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-[28px]">{project.name}</h1>
              <StatusBadge status={status} />
              {!canManage && <span className="badge badge-neutral">{t('projectDetail.memberView')}</span>}
            </div>
            {project.description && (
              <p className="mt-1.5 max-w-2xl whitespace-pre-wrap text-[15px] text-gray-500 dark:text-gray-400">
                {project.description}
              </p>
            )}
          </div>
          {canManage && status === 'active' && (
            <div className="flex shrink-0 gap-2">
              <button type="button" onClick={() => setShowAddMember(true)} className="btn btn-secondary">
                <UserPlusIcon className="h-4 w-4" />
                <span className="hidden sm:inline">{t('members.add')}</span>
              </button>
              <button type="button" onClick={() => setShowCreateRelease(true)} className="btn btn-primary">
                <ArrowUpTrayIcon className="h-4 w-4" />
                {t('releases.new')}
              </button>
            </div>
          )}
        </div>
      </div>

      {status !== 'active' && canManage && (
        <div className="flex flex-col gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200 sm:flex-row sm:items-center sm:justify-between">
          <span>{t(`projectDetail.inactiveNotice.${status}`)}</span>
          <button type="button" onClick={() => setStatusAction('active')} className="btn btn-secondary btn-sm self-start">
            <ArrowPathIcon className="h-4 w-4" />
            {t('projectDetail.restore')}
          </button>
        </div>
      )}

      {tabs.length > 1 && (
        <div className="border-b border-gray-200 dark:border-gray-800">
          <nav className="-mb-px flex gap-6 overflow-x-auto" role="tablist">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`tab shrink-0 ${activeTab === tab.id ? 'tab-active' : ''}`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                  {tab.count !== undefined && (
                    <span className="rounded-full bg-gray-100 px-1.5 text-xs tabular-nums text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      )}

      {/* ---------- Releases ---------- */}
      {activeTab === 'releases' &&
        (releases.length === 0 ? (
          <div className="card">
            <EmptyState
              icon={CubeIcon}
              title={t('releases.empty')}
              text={canManage ? t('releases.emptyManager') : t('releases.emptyMember')}
              action={
                canManage && status === 'active' ? (
                  <button type="button" onClick={() => setShowCreateRelease(true)} className="btn btn-primary">
                    <ArrowUpTrayIcon className="h-4 w-4" />
                    {t('releases.new')}
                  </button>
                ) : undefined
              }
            />
          </div>
        ) : (
          <ol aria-label={t('releases.timeline')}>
            {releases.map((release, index) => (
              <ReleaseCard
                key={release.id}
                release={release}
                projectName={project.name}
                canManage={canManage}
                isLatest={index === 0}
                isLast={index === releases.length - 1}
                onChanged={load}
              />
            ))}
          </ol>
        ))}

      {/* ---------- Members ---------- */}
      {activeTab === 'members' && canManage && (
        <section className="card">
          <div className="flex flex-col gap-3 px-5 pb-3 pt-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="max-w-xl text-sm text-gray-500 dark:text-gray-400">{t('members.explainer')}</p>
            {status === 'active' && (
              <button type="button" onClick={() => setShowAddMember(true)} className="btn btn-secondary btn-sm self-start">
                <UserPlusIcon className="h-4 w-4" />
                {t('members.add')}
              </button>
            )}
          </div>
          {members.length === 0 ? (
            <EmptyState icon={UsersIcon} title={t('members.empty')} text={t('members.emptyText')} compact />
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-gray-800">
              {members.map((member) => (
                <li key={member.id} className="flex items-center gap-3 px-5 py-3">
                  <Avatar name={member.name} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{member.name}</p>
                    <p className="truncate text-xs text-gray-500 dark:text-gray-400">{member.email}</p>
                  </div>
                  <span className="hidden text-xs text-gray-400 sm:block">
                    {t('members.addedOn', { date: formatDate(member.addedAt, language) })}
                  </span>
                  <span className={`badge ${member.role === 'owner' ? 'badge-brand' : 'badge-neutral'}`}>
                    {t(`members.roles.${member.role}`)}
                  </span>
                  {member.role !== 'owner' && (
                    <button
                      type="button"
                      onClick={() => setMemberToRemove(member)}
                      className="btn btn-danger-ghost btn-icon"
                      aria-label={t('members.remove', { name: member.name })}
                      title={t('members.remove', { name: member.name })}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {/* ---------- Sharing ---------- */}
      {activeTab === 'sharing' && canManage && (
        <section className="card">
          <div className="flex flex-col gap-3 px-5 pb-3 pt-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="max-w-xl text-sm text-gray-500 dark:text-gray-400">{t('share.explainer')}</p>
            {status === 'active' && (
              <button type="button" onClick={() => setShowCreateLink(true)} className="btn btn-secondary btn-sm self-start">
                <LinkIcon className="h-4 w-4" />
                {t('share.create')}
              </button>
            )}
          </div>
          {shareLinks.length === 0 ? (
            <EmptyState icon={LinkIcon} title={t('share.empty')} text={t('share.emptyText')} compact />
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-gray-800">
              {shareLinks.map((link) => {
                const state = shareLinkState(link);
                const usable = state === 'ok';
                return (
                  <li key={link.id} className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <code className={`truncate text-sm ${usable ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400 line-through'}`}>
                          {window.location.host}/share/{link.hash}
                        </code>
                        <span className={`badge shrink-0 ${usable ? 'badge-ok' : 'badge-neutral'}`}>
                          {t(`share.state.${state}`)}
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {t('share.meta', {
                          created: formatDate(link.createdAt, language),
                          count: link.downloadCount,
                        })}
                        {link.maxDownloads ? ` — ${t('share.limit', { max: link.maxDownloads })}` : ''}
                        {link.expiresAt ? ` — ${t('share.expires', { date: formatDate(link.expiresAt, language) })}` : ''}
                      </p>
                    </div>
                    <div className="flex shrink-0 gap-1">
                      <button type="button" onClick={() => setVisitorsLink(link)} className="btn btn-ghost btn-sm">
                        <UsersIcon className="h-4 w-4" />
                        {t('share.visitors')}
                      </button>
                      {usable && (
                        <>
                          <button type="button" onClick={() => copyLink(link.hash)} className="btn btn-secondary btn-sm">
                            <ClipboardDocumentIcon className="h-4 w-4" />
                            {t('share.copy')}
                          </button>
                          <button
                            type="button"
                            onClick={() => setLinkToDeactivate(link)}
                            className="btn btn-danger-ghost btn-icon"
                            aria-label={t('share.deactivate')}
                            title={t('share.deactivate')}
                          >
                            <NoSymbolIcon className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}

      {/* ---------- Settings ---------- */}
      {activeTab === 'settings' && canManage && (
        <section className="card divide-y divide-gray-100 dark:divide-gray-800">
          <SettingRow
            title={t('projectDetail.editDetails')}
            text={t('projectDetail.editDetailsText')}
            action={
              <button type="button" onClick={() => setShowEditProject(true)} className="btn btn-secondary btn-sm">
                <PencilSquareIcon className="h-4 w-4" />
                {t('common.edit')}
              </button>
            }
          />
          {status === 'active' && (
            <SettingRow
              title={t('projectDetail.archive')}
              text={t('projectDetail.archiveText')}
              action={
                <button type="button" onClick={() => setStatusAction('archived')} className="btn btn-secondary btn-sm">
                  <ArchiveBoxIcon className="h-4 w-4" />
                  {t('projectDetail.archive')}
                </button>
              }
            />
          )}
          {status !== 'active' && (
            <SettingRow
              title={t('projectDetail.restore')}
              text={t('projectDetail.restoreText')}
              action={
                <button type="button" onClick={() => setStatusAction('active')} className="btn btn-secondary btn-sm">
                  <ArrowPathIcon className="h-4 w-4" />
                  {t('projectDetail.restore')}
                </button>
              }
            />
          )}
          {status !== 'deleted' && (
            <SettingRow
              title={t('projectDetail.delete')}
              text={t('projectDetail.deleteText')}
              danger
              action={
                <button type="button" onClick={() => setStatusAction('deleted')} className="btn btn-danger btn-sm">
                  <TrashIcon className="h-4 w-4" />
                  {t('projectDetail.delete')}
                </button>
              }
            />
          )}
        </section>
      )}

      {/* ---------- Dialogs ---------- */}
      {showAddMember && (
        <AddMemberModal
          projectId={project.id}
          onClose={() => setShowAddMember(false)}
          onSuccess={() => {
            setShowAddMember(false);
            setActiveTab('members');
            load();
          }}
        />
      )}

      {showCreateRelease && (
        <CreateReleaseModal
          projectId={project.id}
          suggestedVersion={nextVersion(releases[0]?.version)}
          onClose={() => setShowCreateRelease(false)}
          onSuccess={() => {
            setShowCreateRelease(false);
            setActiveTab('releases');
            load();
          }}
        />
      )}

      {showEditProject && (
        <EditProjectModal
          project={project}
          onClose={() => setShowEditProject(false)}
          onSaved={() => {
            setShowEditProject(false);
            load();
          }}
        />
      )}

      {showCreateLink && (
        <CreateShareLinkModal
          projectId={project.id}
          onClose={() => setShowCreateLink(false)}
          onCreated={async (link) => {
            setShowCreateLink(false);
            await copyLink(link.hash);
            load();
          }}
        />
      )}

      <ConfirmDialog
        isOpen={memberToRemove !== null}
        onClose={() => setMemberToRemove(null)}
        onConfirm={removeMember}
        title={t('members.removeTitle')}
        message={memberToRemove ? t('members.removeConfirm', { name: memberToRemove.name }) : ''}
        confirmText={t('members.removeButton')}
        loading={removingMember}
      />

      <ConfirmDialog
        isOpen={statusAction !== null}
        onClose={() => setStatusAction(null)}
        onConfirm={() => statusAction && changeStatus(statusAction)}
        title={statusAction ? t(`projectDetail.confirm.${statusAction}.title`) : ''}
        message={statusAction ? t(`projectDetail.confirm.${statusAction}.text`) : ''}
        confirmText={statusAction ? t(`projectDetail.confirm.${statusAction}.button`) : ''}
        type={statusAction === 'deleted' ? 'danger' : 'info'}
        loading={actionLoading}
      />

      <ConfirmDialog
        isOpen={linkToDeactivate !== null}
        onClose={() => setLinkToDeactivate(null)}
        onConfirm={deactivateLink}
        title={t('share.deactivateTitle')}
        message={t('share.deactivateConfirm')}
        confirmText={t('share.deactivate')}
        loading={actionLoading}
      />

      {visitorsLink && (
        <PublicDownloadsModal link={visitorsLink} releaseVersions={releaseVersions} onClose={() => setVisitorsLink(null)} />
      )}
    </div>
  );
};

const SettingRow: React.FC<{ title: string; text: string; action: React.ReactNode; danger?: boolean }> = ({
  title,
  text,
  action,
  danger,
}) => (
  <div className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
    <div>
      <p className={`text-sm font-semibold ${danger ? 'text-red-700 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>{title}</p>
      <p className="mt-0.5 max-w-lg text-sm text-gray-500 dark:text-gray-400">{text}</p>
    </div>
    <div className="shrink-0">{action}</div>
  </div>
);

const EditProjectModal: React.FC<{ project: Project; onClose: () => void; onSaved: () => void }> = ({
  project,
  onClose,
  onSaved,
}) => {
  const { t } = useLanguage();
  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await projectService.updateProject(project.id, { name: name.trim(), description: description.trim() });
      await shareService.syncPublicProject(project.id).catch((err) => console.warn(err));
      toast.success(t('projectDetail.saved'));
      onSaved();
    } catch (err) {
      console.error('Error updating project:', err);
      setError(t('common.genericError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={t('projectDetail.editDetails')}
      onClose={onClose}
      busy={saving}
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-secondary" disabled={saving}>
            {t('common.cancel')}
          </button>
          <button type="submit" form="edit-project" className="btn btn-primary" disabled={saving || !name.trim()}>
            {saving ? t('common.working') : t('common.saveChanges')}
          </button>
        </>
      }
    >
      <form id="edit-project" onSubmit={save} className="space-y-4">
        <Field label={t('projects.name')} htmlFor="edit-project-name">
          <input id="edit-project-name" className="input" value={name} onChange={(e) => setName(e.target.value)} required maxLength={200} />
        </Field>
        <Field label={t('projects.description')} htmlFor="edit-project-description">
          <textarea
            id="edit-project-description"
            className="input"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>
        {error && <ErrorNote>{error}</ErrorNote>}
      </form>
    </Modal>
  );
};

const CreateShareLinkModal: React.FC<{
  projectId: string;
  onClose: () => void;
  onCreated: (link: ShareLink) => void;
}> = ({ projectId, onClose, onCreated }) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [days, setDays] = useState('');
  const [maxDownloads, setMaxDownloads] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setSaving(true);
    setError('');
    try {
      const dayCount = parseInt(days, 10);
      const max = parseInt(maxDownloads, 10);
      const link = await shareService.createShareLink(projectId, user.uid, {
        expiresAt: dayCount > 0 ? new Date(Date.now() + dayCount * 24 * 60 * 60 * 1000) : undefined,
        maxDownloads: max > 0 ? max : undefined,
      });
      toast.success(t('share.created'));
      onCreated(link);
    } catch (err) {
      console.error('Error creating share link:', err);
      setError(t('share.createFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={t('share.create')}
      description={t('share.createText')}
      onClose={onClose}
      busy={saving}
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-secondary" disabled={saving}>
            {t('common.cancel')}
          </button>
          <button type="submit" form="create-link" className="btn btn-primary" disabled={saving}>
            {saving ? t('common.working') : t('share.createAndCopy')}
          </button>
        </>
      }
    >
      <form id="create-link" onSubmit={create} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label={t('share.validDays')} htmlFor="link-days" hint={t('share.optional')}>
          <input id="link-days" type="number" min={1} className="input" value={days} onChange={(e) => setDays(e.target.value)} />
        </Field>
        <Field label={t('share.maxDownloads')} htmlFor="link-max" hint={t('share.optional')}>
          <input
            id="link-max"
            type="number"
            min={1}
            className="input"
            value={maxDownloads}
            onChange={(e) => setMaxDownloads(e.target.value)}
          />
        </Field>
        {error && (
          <div className="sm:col-span-2">
            <ErrorNote>{error}</ErrorNote>
          </div>
        )}
      </form>
    </Modal>
  );
};

export default ProjectDetail;
